import React, { useState } from "react";
import FilterChip from "@ui-components/Searchbar/FilterChip";

interface ExcludeFilterProps {
  chips: string[];
  onChange: (chips: string[]) => void;
  /** Width of the field's column, e.g. `flex-1 min-w-[240px]`. */
  className?: string;
  /** The label's classes, to match the neighbouring filter labels of the page. */
  labelClassName?: string;
}

/**
 * Chips that take lines out of the table: a MSGID equal to the chip, or a
 * message containing it, ignoring case (see `excludeRecords`). Enter adds the
 * typed text; the same chip, in any case, is added once.
 */
export function ExcludeFilter({ chips, onChange, className = "w-full",
  labelClassName = "block mb-1 text-sm font-medium text-semantic-text-primary",
}: ExcludeFilterProps) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const chip = draft.trim();
    setDraft("");
    if (!chip) return;
    if (chips.some((existing) => existing.toLowerCase() === chip.toLowerCase())) return;
    onChange([...chips, chip]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    add();
  };

  return (
    <div className={`flex flex-col ${className}`} role="group" aria-label="Exclude">
      <span className={labelClassName}>Exclude</span>
      {/* One field, like GenericSearchbar: chips and input share the border, and the
          chips wrap inside it instead of pushing the filter row onto another line. */}
      <div className="flex min-h-8 w-full flex-wrap items-center gap-1 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt px-2 py-0 focus-within:border-semantic-border-brand-secondary">
        {chips.map((chip) => (
          <FilterChip
            key={chip}
            removeLabel={`Remove ${chip}`}
            onRemove={() => onChange(chips.filter((existing) => existing !== chip))}
          >
            <span className="truncate">{chip}</span>
          </FilterChip>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add…"
          aria-label="Add exclusion"
          className="h-6 min-w-[80px] flex-1 bg-transparent text-xs text-semantic-text-primary placeholder-gray-400 outline-none"
        />
      </div>
    </div>
  );
}

export default ExcludeFilter;
